import { experimental_extendTheme as extendTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import cssVarJson from './export.json';
import { fixThemeOptions } from '../../utils/theme';
import SofiaProBold from './fonts/sofia-pro/Sofia Pro Bold Az.otf';
import SofiaProRegular from './fonts/sofia-pro/Sofia Pro Regular Az.otf';
const CSS_VAR_PREFIX = 'montu'; // Needs to be unique to support theme nesting
const baseFontFamily = 'Sofia Pro, Helvetica, Arial, sans-serif';
const customizations = {
    cssVarPrefix: CSS_VAR_PREFIX,
    spacing: (factor) => `${0.25 * factor}rem`,
    typography: {
        fontFamily: baseFontFamily
    },
    components: {
        // MuiAlert overrides to fix the broken logic in extend theme.
        MuiAlert: {
            styleOverrides: {
                standardError: {
                    backgroundColor: `var(--${CSS_VAR_PREFIX}-palette-error-light)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-error-dark)` // Different color for message text
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                standardWarning: {
                    backgroundColor: '#FFF4E5',
                    color: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-warning-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                standardInfo: {
                    backgroundColor: '#E5F6FD',
                    color: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-info-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                standardSuccess: {
                    backgroundColor: '#EDF7ED',
                    color: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-success-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                outlinedError: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-error-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                outlinedWarning: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-warning-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                outlinedInfo: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-info-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                },
                outlinedSuccess: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-success-dark)`
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit'
                        }
                    }
                }
            }
        },
        MuiCssBaseline: {
            styleOverrides: `
        @font-face {
          font-family: 'Sofia Pro';
          font-style: normal;
          src: url(${SofiaProRegular}) format('opentype');
          font-weight: 100 500;
        }
        @font-face {
          font-family: 'Sofia Pro';
          font-style: normal;
          src: url(${SofiaProBold}) format('opentype');
          font-weight: 600 900;
        }
      `
        },
        // Temporary fixes to the export. Once the figma is fixed and exported properly, these should be removed
        MuiChip: {
            styleOverrides: {
                label: {
                    fontSize: '0.875rem',
                    fontWeight: 700
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                sizeLarge: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '8px 11px' : '8px 22px' // Export bug causing button hover padding change
                }),
                sizeMedium: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '6px 8px' : '6px 16px'
                }),
                sizeSmall: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '4px 5px' : '4px 10px'
                })
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: `1px solid var(--${CSS_VAR_PREFIX}-palette-divider)`
                },
                head: {
                    fontWeight: 600
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:last-child': {
                        borderBottom: `1px solid var(--${CSS_VAR_PREFIX}-palette-divider)`
                    }
                }
            }
        }
    }
};
export const montuJSON = fixThemeOptions(deepmerge(cssVarJson, customizations));
export const montu = extendTheme(montuJSON);
export default montu;
//# sourceMappingURL=data:application/json;base64,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